import React, { useEffect, useState, useRef } from "react";
import { motion, useMotionValue, useTransform, animate, AnimatePresence } from "framer-motion";
import playIcon from '../../assets/play.png';
import pauseIcon from '../../assets/pause.png';

const TrendsCarousel = ({ 
  queue, 
  currentIndex, 
  hydratedSongs, 
  onNext, 
  onPrevious, 
  onRate, 
  isPlaying, 
  togglePlayPause,
  playSource,
  startTrendsPlayback
}) => {
  // Refs and State for calculating dynamic card height
  const carouselRef = useRef(null);
  const isHandledRef = useRef(false);
  const isGestureHandledRef = useRef(false);
  const [cardHeight, setCardHeight] = useState(0);
  
  // Motion values
  // NEW: `containerY` controls the entire reel's vertical position.
  const containerY = useMotionValue(0);
  // `x` still controls the horizontal drag for rating the CURRENT card.
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-25, 25]);

  // Local state for the rating flash animation
  const [flash, setFlash] = useState({ active: false, color: '' });

  const [showPlayPause, setShowPlayPause] = useState(false);
  const animationTimer = useRef(null);

  const handleTapToPlayPause = () => {
      // ✅ Check if we need to switch sources first
      const isSwitchingSource = playSource !== 'Trends';

      if (isSwitchingSource) {
          startTrendsPlayback();
      } else {
          // If already on Trends, just act as normal toggle
          togglePlayPause();
      }
      
      // Manage visual feedback as before
      setShowPlayPause(true);
      if (animationTimer.current) {
          clearTimeout(animationTimer.current);
      }
      animationTimer.current = setTimeout(() => {
          setShowPlayPause(false);
      }, 1300);
  };

  // --- HOOKS ---

  // 1. Measure the height of the carousel container on mount.
  useEffect(() => {
    if (carouselRef.current) {
      setCardHeight(carouselRef.current.offsetHeight);
    }
  }, []);

  // 2. The CORE animation hook. This runs whenever the song changes.
  // It smoothly animates the entire container to the new song's position.
  useEffect(() => {
    if (cardHeight > 0) {
      const targetY = -currentIndex * cardHeight;
      animate(containerY, targetY, {
        type: "spring",
        stiffness: 300,
        damping: 30,
      });
    }
    // Also reset horizontal drag motion value when the index changes.
    x.set(0);
  }, [currentIndex, cardHeight, containerY, x]);

  // 🌟 DESKTOP KEYBOARD CONTROLS (With Animated Card Tilt & Rating Flash)
  // File: src/components/trends/TrendsCarousel.js (Keyboard useEffect excerpt)

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Ignore key events if the user is typing in an input field or text area
      const activeTag = document.activeElement?.tagName?.toLowerCase();
      if (activeTag === 'input' || activeTag === 'textarea') return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          if (currentIndex < queue.length - 1) {
            onNext();
          }
          break;

        case "ArrowUp":
          e.preventDefault();
          if (currentIndex > 0) {
            onPrevious();
          }
          break;

        case "ArrowRight": // LIKE (Tilt Right + Pink Flash)
          e.preventDefault();
          
          // 🌟 FIX: Use duration & ease for multi-keyframe arrays [start, peek, return]
          animate(x, [0, 140, 0], {
            duration: 0.35,
            ease: [0.32, 0.72, 0, 1], // Custom snappy spring-like cubic bezier
          });

          setFlash({ active: true, color: 'rgba(255, 0, 204, 1)' });
          onRate("liked");
          break;

        case "ArrowLeft": // DISLIKE (Tilt Left + Blue Flash)
          e.preventDefault();

          // 🌟 FIX: Use duration & ease for multi-keyframe arrays [start, peek, return]
          animate(x, [0, -140, 0], {
            duration: 0.35,
            ease: [0.32, 0.72, 0, 1], // Custom snappy spring-like cubic bezier
          });

          setFlash({ active: true, color: 'rgba(135, 206, 235, 1)' });
          onRate("disliked");
          break;

        case " ": // Spacebar for Play/Pause toggle
          e.preventDefault();
          handleTapToPlayPause();
          break;

        default:
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [currentIndex, queue.length, onNext, onPrevious, onRate, x]);


  // --- DRAG HANDLERS ---

  // 1. Reset gesture lock whenever a new drag sequence starts
  const handleDragStart = () => {
    isGestureHandledRef.current = false;
  };
  
  // 2. Handles HORIZONTAL drag for rating (LIKE/DISLIKE)
  const handleHorizontalDragEnd = (event, info) => {
    const { offset, velocity } = info;

    // 🔒 Block if this gesture was already handled (e.g. by vertical action)
    if (isGestureHandledRef.current) return;

    const absX = Math.abs(offset.x);
    const absY = Math.abs(offset.y);

    // 🛑 DOMINANCE CHECK: If vertical movement exceeds horizontal movement, ignore horizontal rating!
    if (absY > absX) {
      animate(x, 0, { type: "spring", stiffness: 500, damping: 50 });
      return; 
    }

    const swipePower = absX * velocity.x;
    let rating = null;

    if (swipePower > 10000) rating = "liked";
    else if (swipePower < -10000) rating = "disliked";

    if (rating) {
      // 🔒 Claim the gesture lock so vertical action cannot fire!
      isGestureHandledRef.current = true;

      setFlash({ 
        active: true, 
        color: rating === 'liked' ? 'rgba(255, 0, 204, 1)' : 'rgba(135, 206, 235, 1)' 
      });

      onRate(rating);

      animate(x, 0, { 
        type: "spring", 
        stiffness: 500, 
        damping: 50,
      });
    } else {
      // Reset position if swipe power threshold was not met
      animate(x, 0, { type: "spring", stiffness: 500, damping: 50 });
    }
  };

  // 3. Handles VERTICAL drag for the reel (NEXT/PREVIOUS)
  const handleVerticalDragEnd = (event, info) => {
    const { offset, velocity } = info;

    // 🔒 Block if this gesture was already claimed (e.g. by horizontal rating)
    if (isGestureHandledRef.current) return;

    const absX = Math.abs(offset.x);
    const absY = Math.abs(offset.y);

    // 🛑 DOMINANCE CHECK: If horizontal movement exceeds vertical movement, ignore vertical song switch!
    if (absX > absY) {
      return;
    }

    const swipeThreshold = cardHeight * 0.15;
    const velocityThreshold = 200; 
      
    let targetIndex = currentIndex;

    if (offset.y < -swipeThreshold || velocity.y < -velocityThreshold) {
      targetIndex = Math.min(currentIndex + 1, queue.length - 1);
    } else if (offset.y > swipeThreshold || velocity.y > velocityThreshold) {
      targetIndex = Math.max(currentIndex - 1, 0);
    }

    // If a valid song index change is detected
    if (targetIndex !== currentIndex) {
      // 🔒 Claim the gesture lock!
      isGestureHandledRef.current = true;

      const targetY = -targetIndex * cardHeight;

      animate(containerY, targetY, {
        type: "spring",
        stiffness: 300,
        damping: 30,
        velocity: velocity.y,
        onComplete: () => {
          if (targetIndex > currentIndex) {
            onNext();
          } else {
            onPrevious();
          }
        }
      });
    } else {
      // Snap back to current card if threshold was not reached
      const targetY = -currentIndex * cardHeight;
      animate(containerY, targetY, { type: "spring", stiffness: 300, damping: 30 });
    }
  };

  


  // --- RENDER LOGIC ---

  // 5. Define the "moving window" of cards to render.
  // We render 2 cards before and 2 after the current one.
  const renderWindow = 2;
  const startIndex = Math.max(0, currentIndex - renderWindow);
  const endIndex = Math.min(queue.length - 1, currentIndex + renderWindow);

  // Create a slice of the queue to render.
  const songsToRender = queue.slice(startIndex, endIndex + 1).map((queueItem, i) => ({
    ...queueItem,
    index: startIndex + i
  }));

  return (
    // The ref is attached here to measure the container's height.
    <div 
      ref={carouselRef} 
      className="absolute inset-0 overflow-hidden bg-[#101010]"
      onClick={handleTapToPlayPause}
    >
      {/* Flash animation layer */}
      {flash.active && (
        <motion.div
          className="absolute inset-0 z-20 pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ opacity: [0, 1, 0] }}
          transition={{ duration: 0.4 }}
          style={{ backgroundColor: flash.color }}
          onAnimationComplete={() => setFlash({ active: false, color: '' })}
        />
      )}

      {/* 6. The main draggable "movie reel" container */}
      {cardHeight > 0 && (
        <motion.div
          className="relative w-full h-full z-10"
          drag="y"
          dragDirectionLock
          //dragMomentum={false}
          dragConstraints={{
            top: -(queue.length - 1) * cardHeight,
            bottom: 0,
          }}
          style={{ y: containerY }}
          onDragStart={handleDragStart}
          onDragEnd={handleVerticalDragEnd}
        >
          {/* 7. Map over the window of songs and position them vertically */}
          {songsToRender.map(({ id, isBoosted, index }) => {
            const songDetails = hydratedSongs[id];
            const isCurrent = index === currentIndex;

            // Define shared background styles for reuse.
            const backgroundStyles = {
              backgroundImage: songDetails?.resolvedArtworkUrl ? `url(${songDetails.resolvedArtworkUrl})` : 'none',
              backgroundColor: songDetails?.resolvedArtworkUrl ? 'transparent' : '#191919',
            };

            return (
              <motion.div
                key={id}
                className="absolute w-full h-full"
                // Position each card in the reel based on its index
                style={{
                  top: index * cardHeight,
                  height: cardHeight,
                }}
              >
                {/* ✅ ADD THIS BLOCK: Conditionally render the sponsored pill */}
                {isBoosted && (
                    <span className="absolute top-20 right-4 bg-pink-100 text-pink-700 text-xs font-semibold px-2 py-0.5 rounded-full z-10">
                        Sponsored
                    </span>
                )}
                {isCurrent ? (
                  // The CURRENT card gets the horizontal drag functionality
                  <motion.div
                    className="w-full h-full bg-cover bg-center bg-no-repeat"
                    drag="x"
                    dragDirectionLock
                    dragPropagation={false} 
                    dragConstraints={{ left: 0, right: 0 }}
                    style={{ x, rotate, ...backgroundStyles }}
                    onDragStart={handleDragStart}
                    onDragEnd={handleHorizontalDragEnd}
                  >
                    {!songDetails?.resolvedArtworkUrl && (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-white border-solid"></div>
                      </div>
                    )}
                  </motion.div>
                ) : (
                  // Previous/Next cards are static divs
                  <div
                    className="w-full h-full bg-cover bg-center bg-no-repeat"
                    style={backgroundStyles}
                  >
                    {!songDetails?.resolvedArtworkUrl && (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-white border-solid"></div>
                      </div>
                    )}
                  </div>
                )}
              </motion.div>
            );
          })}
        </motion.div>
      )}

      <AnimatePresence>
          {showPlayPause && (
              <motion.div
                  className="absolute inset-0 flex items-center justify-center z-30 pointer-events-none"
                  initial={{ opacity: 0, scale: 1.5 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 1.5 }}
                  transition={{ 
                      opacity: { duration: 0.15 }, // Enter animation (half the time)
                      scale: { duration: 0.05 },   // Enter animation
                      default: { duration: 0.80 }  // Exit animation (1.5x slower)
                  }}
              >
                  <img 
                      src={(playSource !== 'Trends' || isPlaying) ? playIcon : pauseIcon} 
                      alt={(playSource !== 'Trends' || isPlaying) ? "Play" : "Pause"}
                      className="w-20 h-20"
                  />
              </motion.div>
          )}
      </AnimatePresence>
    </div>
  );
};

export default TrendsCarousel;